/**
 * Per-panel loading shapes - thin wrappers over the shared
 * `PanelLoading` card. Each export pins the icon + canonical title for
 * one step (Text → FileText "Preparing Text", Mindmap → Brain
 * "Preparing Mindmap", etc.) and reuses the shared neutral spinner +
 * spacing.
 *
 * No rainbow per-step accents - per-panel identity rides on the icon
 * alone. Two orthogonal state signals:
 *
 *   - `pending` - pipeline state. True when the step is queued
 *     (`'pending'`) rather than actively processing. Swaps the title
 *     between "Queued - X" and "Preparing X". Only meaningful when
 *     `mode === 'generating'`.
 *
 *   - `mode` - *why* we're loading. `'generating'` (default) means the
 *     pipeline is producing the artifact; copy is generation-flavored
 *     ("Preparing X" / "Writing a structured walkthrough..."). `'fetching'`
 *     means the artifact is already done and we're just GETting it from
 *     the backend; copy shifts to "Loading X" / "Fetching your x." so
 *     the user can tell a 1s network round-trip from a multi-minute
 *     pipeline run.
 */

import {
  FileText,
  Brain,
  Video,
  Music,
  Layers3,
  BookCheck,
  BookMarked,
  FolderOpen,
  Sparkles,
  WalletCards,
  HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { PanelLoading } from '../common/PanelLoading';
import { Skeleton } from '@ai-tutor/ui';

// ---------------------------------------------------------------------------
// Wrapper helper - every panel skeleton routes through here so the
// title format ("Preparing X" / "Queued - X") stays consistent.
// ---------------------------------------------------------------------------

interface PanelSkeletonProps {
  /** True when the step is `pending` (queued) rather than `processing`. */
  pending?: boolean;
  /**
   * `'generating'` (default) - pipeline is producing the artifact.
   * `'fetching'` - artifact is already done; we're GETting it from
   * the backend. Switches the title to "Loading X" and the
   * description to "Fetching your x." so a 1s GET doesn't read as a
   * multi-minute pipeline run.
   */
  mode?: 'generating' | 'fetching';
}

function makeSkeleton({
  icon,
  label,
  description,
}: {
  icon: LucideIcon;
  label: string;
  description: string;
}) {
  return function SpecificSkeleton({
    pending = false,
    mode = 'generating',
  }: PanelSkeletonProps) {
    const title =
      mode === 'fetching'
        ? `Loading ${label}`
        : pending
          ? `Queued - ${label}`
          : `Preparing ${label}`;
    const body =
      mode === 'fetching'
        ? `Fetching your ${label.toLowerCase()}.`
        : description;
    return <PanelLoading icon={icon} title={title} description={body} />;
  };
}

// ---------------------------------------------------------------------------
// PromptOverviewSkeleton - stays as a small inline shimmer (the
// SourcesPanel sidebar is dense; a full PanelLoading card overflows).
// ---------------------------------------------------------------------------

export function PromptOverviewSkeleton() {
  return (
    <div className='space-y-4 p-4'>
      <Skeleton className='h-7 w-3/4' />
      <div className='flex gap-2'>
        <Skeleton className='h-5 w-16 rounded-full' />
        <Skeleton className='h-5 w-20 rounded-full' />
        <Skeleton className='h-5 w-14 rounded-full' />
      </div>
      <div className='space-y-2'>
        <Skeleton className='h-3 w-full' />
        <Skeleton className='h-3 w-5/6' />
        <Skeleton className='h-3 w-4/6' />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Center-panel skeletons - one wrapper per step.
// ---------------------------------------------------------------------------

export const TextLessonSkeleton = makeSkeleton({
  icon: FileText,
  label: 'Text',
  description: 'Writing a structured walkthrough with citations and key terms.',
});

export const MindmapSkeleton = makeSkeleton({
  icon: Brain,
  label: 'Overview',
  description: 'Building an interactive concept map from the lesson context.',
});

export const HighlightsSkeleton = makeSkeleton({
  icon: Layers3,
  label: 'Highlights',
  description: 'Distilling each concept into swipeable summary cards.',
});

export const VideoSkeleton = makeSkeleton({
  icon: Video,
  label: 'Video',
  description: 'Generating an AI-narrated video walkthrough.',
});

export const PodcastSkeleton = makeSkeleton({
  icon: Music,
  label: 'Podcast',
  description: 'Recording a two-host conversational walkthrough.',
});

export const QuizSkeleton = makeSkeleton({
  icon: BookCheck,
  label: 'Quiz',
  description: 'Drafting concept-tagged questions for your revision plan.',
});

export const FlashcardsSkeleton = makeSkeleton({
  icon: WalletCards,
  label: 'Flashcards',
  description: 'Distilling each concept into a flip-card prompt and answer.',
});

export const GlossarySkeleton = makeSkeleton({
  icon: BookMarked,
  label: 'Glossary',
  description: 'Extracting key terms and definitions from your lesson.',
});

export const ExtrasSkeleton = makeSkeleton({
  icon: FolderOpen,
  label: 'Extras',
  description: 'Curating additional reading and reference material.',
});

export const DoubtsSkeleton = makeSkeleton({
  icon: HelpCircle,
  label: 'Doubts',
  description: 'Gathering the questions you flagged while chatting.',
});

export const GenericPanelSkeleton = makeSkeleton({
  icon: Sparkles,
  label: 'this section',
  description: 'Working on it. Content will appear automatically when ready.',
});
